import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  CreditCard,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  MessageSquare,
  Bot,
  ArrowRight,
  Calendar,
  DollarSign,
  FileCheck
} from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { StatusBadge } from '../ui/StatusBadge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { invoiceService } from '../../lib/database/invoices';
import { quoteService } from '../../lib/database/quotes';
import { paymentService } from '../../lib/database/payments';
import { useAuth } from '../../hooks/useAuth';

export const ClientPortal: React.FC = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<any[]>([]);
  const [quotes, setQuotes] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        setLoading(true);
        
        // Fetch all data in parallel
        const [invoicesRes, quotesRes, paymentsRes] = await Promise.all([
          invoiceService.getInvoices(),
          quoteService.getQuotes(),
          paymentService.getPayments()
        ]);
        
        // Filter data for current client
        if (invoicesRes.data) {
          const clientInvoices = invoicesRes.data.filter(invoice => 
            invoice.client?.email === user?.email
          );
          setInvoices(clientInvoices);
        }
        
        if (quotesRes.data) {
          const clientQuotes = quotesRes.data.filter(quote => 
            quote.client?.email === user?.email
          );
          setQuotes(clientQuotes);
        }
        
        if (paymentsRes.data) {
          const clientPayments = paymentsRes.data.filter(payment => 
            payment.invoice?.client?.email === user?.email
          );
          setPayments(clientPayments);
        }
      } catch (error) {
        console.error('Error fetching client data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.email) {
      fetchClientData();
    }
  }, [user?.email]);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="animate-pulse">
          <div className="bg-gray-200 rounded-2xl h-32 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl h-32"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const totalPaid = payments.reduce((sum, payment) => sum + Number(payment.amount), 0);
  
  const totalOutstanding = invoices
    .filter(inv => inv.status !== 'paid')
    .reduce((sum, inv) => sum + Number(inv.total), 0);

  const overdueInvoices = invoices.filter(inv => 
    inv.status === 'overdue' || 
    (inv.status === 'sent' && new Date(inv.due_date) < new Date())
  );

  const nextPayment = overdueInvoices
    .sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())[0];

  const pendingQuotes = quotes.filter(q => q.status === 'sent');

  const handlePayInvoice = (invoice: any) => {
    alert(`Payment gateway would open for invoice ${invoice.invoice_number} - ${formatCurrency(Number(invoice.total))}`);
  };

  const handleRequestQuote = () => {
    alert('This would open a form to request a new quote in a real application.');
  };

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-2">Welcome back, {user?.full_name || 'Client'}!</h1>
        <p className="text-primary-100 text-lg">
          Here's your account overview and recent activity
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card hover className="text-center">
          <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-6 h-6 text-success-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(totalPaid)}</h3>
          <p className="text-gray-600">Total Paid</p>
        </Card>

        <Card hover className="text-center">
          <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <Clock className="w-6 h-6 text-warning-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{formatCurrency(totalOutstanding)}</h3>
          <p className="text-gray-600">Outstanding</p>
        </Card>

        <Card hover className="text-center">
          <div className="w-12 h-12 bg-error-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-error-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900">
            {nextPayment ? formatCurrency(Number(nextPayment.total)) : '₹0'}
          </h3>
          <p className="text-gray-600">Next Payment Due</p>
          {nextPayment && (
            <p className="text-sm text-error-600 font-medium mt-1">
              Due {formatDate(nextPayment.due_date)}
            </p>
          )}
        </Card>
      </div>

      {/* Pending Quotes Alert */}
      {pendingQuotes.length > 0 && (
        <Card className="bg-warning-50 border-warning-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-warning-100 rounded-lg flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-warning-600" />
              </div>
              <div>
                <h3 className="font-semibold text-warning-800">Quotes Awaiting Your Response</h3>
                <p className="text-warning-700">
                  You have {pendingQuotes.length} quote{pendingQuotes.length !== 1 ? 's' : ''} waiting for your approval
                </p>
              </div>
            </div>
            <Link to="/client-portal/quotes">
              <Button variant="warning" size="sm">
                Review Quotes
              </Button>
            </Link>
          </div>
        </Card>
      )}

      {/* Recent Invoices */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Recent Invoices</h2>
          <Link to="/client-portal/invoices">
            <Button variant="ghost" size="sm" className="flex items-center">
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
        
        <div className="space-y-4">
          {invoices.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No invoices yet. Your invoices will appear here when they are created.</p>
            </div>
          ) : (
            invoices.slice(0, 3).map((invoice) => (
              <div
                key={invoice.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{invoice.invoice_number}</p>
                    <p className="text-sm text-gray-600">Due {formatDate(invoice.due_date)}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(Number(invoice.total))}</p>
                    <StatusBadge status={invoice.status} />
                  </div>
                  <div className="flex space-x-2">
                    <Link to={`/client-portal/invoices`}>
                      <Button variant="ghost" size="sm">
                        View
                      </Button>
                    </Link>
                    {invoice.status !== 'paid' && (
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={() => handlePayInvoice(invoice)}
                      >
                        Pay Now
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>

      {/* Recent Activity */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
        </div>
        
        <div className="space-y-4">
          {payments.length === 0 && quotes.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No recent activity. Your activity will appear here as you interact with the portal.</p>
            </div>
          ) : (
            <>
              {payments.slice(0, 2).map((payment) => (
                <div
                  key={payment.id}
                  className="flex items-center justify-between p-4 bg-success-50 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-success-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-success-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Payment Recorded</p>
                      <p className="text-sm text-gray-600">
                        For invoice {payment.invoice?.invoice_number || 'Unknown'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="font-semibold text-success-600">{formatCurrency(Number(payment.amount))}</p>
                    <p className="text-sm text-gray-600">{formatDate(payment.payment_date)}</p>
                  </div>
                </div>
              ))}

              {quotes.slice(0, 2).map((quote) => (
                <div
                  key={quote.id}
                  className="flex items-center justify-between p-4 bg-blue-50 rounded-lg"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Quote {quote.quote_number}</p>
                      <p className="text-sm text-gray-600">
                        {quote.status === 'sent' ? 'Awaiting your response' : `Status: ${quote.status}`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{formatCurrency(Number(quote.total))}</p>
                    <StatusBadge status={quote.status} />
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card hover>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-primary-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Make a Payment</h3>
              <p className="text-gray-600 text-sm">Pay your outstanding invoices</p>
            </div>
            <Link to="/client-portal/invoices">
              <Button variant="primary" size="sm">
                Pay Now
              </Button>
            </Link>
          </div>
        </Card>

        <Card hover>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-secondary-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Request Quote</h3>
              <p className="text-gray-600 text-sm">Get a quote for new services</p>
            </div>
            <Button 
              variant="secondary" 
              size="sm"
              onClick={handleRequestQuote}
            >
              Request
            </Button>
          </div>
        </Card>
      </div>

      {/* Support and AI Assistant */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">Need Help?</h3>
                <p className="text-blue-700 text-sm">Contact our support team</p>
              </div>
            </div>
            <Link to="/client-portal/support">
              <Button variant="primary" className="bg-blue-600 hover:bg-blue-700">
                Support Center
              </Button>
            </Link>
          </div>
        </Card>

        <Card className="bg-gradient-to-r from-accent-50 to-accent-100 border-accent-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
                <Bot className="w-5 h-5 text-accent-600" />
              </div>
              <div>
                <h3 className="font-semibold text-accent-900">AI Assistant</h3>
                <p className="text-accent-700 text-sm">Get instant answers</p>
              </div>
            </div>
            <Link to="/client-portal/ai-assistant">
              <Button variant="primary" className="bg-accent-600 hover:bg-accent-700">
                Chat Now
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};